import {
  type ArtifactEventPayload,
  type FollowUpView,
  type StatusEventPayload,
  TURN_ERROR_INCOMPLETE,
  TURN_ERROR_INTERRUPTED,
  TURN_ERROR_SHUTTING_DOWN,
  TURN_ERROR_STOPPED,
  TURN_EVENT_ARTIFACT,
  TURN_EVENT_DONE,
  TURN_EVENT_ERROR,
  TURN_EVENT_MESSAGE,
  TURN_EVENT_STATUS,
  TURN_EVENT_TEXT,
  TURN_EVENT_TOOL_RESULT,
  errorEventPayloadSchema,
  messageEventPayloadSchema,
  parseArtifactEventPayload,
  parseStatusEventPayload,
} from '@sigora/domain';
import type { UIMessage, UIMessageChunk } from 'ai';

import type { SseFrame } from './sse.ts';

/**
 * The data parts this product shows. `status` is transient — it drives the "building your
 * guide…" affordance and is nothing the conversation should still be saying afterwards — while
 * `artifact` is persistent and keyed by the artifact's id, which is what makes an agent rewriting a
 * document render as that document changing rather than as a pile of snapshots in the transcript.
 * `follow-up` is never streamed: it is a Brief follow-up's answer, read from its own route and drawn
 * where a reply would be.
 */
export type SigoraDataParts = {
  status: StatusEventPayload;
  artifact: ArtifactEventPayload;
  'follow-up': FollowUpView;
};

export type SigoraUIMessage = UIMessage<never, SigoraDataParts>;

/**
 * What the user is told when a turn ends badly. Branching on the code from the terminal event's
 * payload, never on the turn row's `error` column: that column holds flattened display text, so
 * a UI reading it would be matching prose. These are the spine's own codes; anything else is a
 * refusal or a bug the agent reported, whose message is already meant for a person.
 *
 * Codes the agent runtime adds — a spend cap, an iteration cap — fall through to that message
 * rather than being anticipated here. A `case` for a constant this package cannot import yet
 * would have to hardcode its value, and a wrong guess is worse than the fallback: it reads as
 * handled while silently never matching.
 */
export const terminalErrorMessage = (code: string, message: string): string => {
  switch (code) {
    case TURN_ERROR_STOPPED:
      return 'Cancelled.';
    case TURN_ERROR_SHUTTING_DOWN:
      return 'That reply was interrupted by a restart. Send it again and it will pick up.';
    case TURN_ERROR_INTERRUPTED:
      return 'That reply stopped partway through. Send it again.';
    case TURN_ERROR_INCOMPLETE:
      return 'Part of that reply was lost on the way. Ask again to see the whole of it.';
    default:
      return message;
  }
};

/**
 * Turns the spine's event frames into the chunks the chat state machine consumes.
 *
 * Stateful rather than a pure per-frame function because the text protocol is bracketed: deltas
 * belong to a block that has to be opened before the first one and closed after the last, and
 * only the mapper knows whether it has opened one yet. Everything else is a straight translation.
 *
 * A frame it cannot read is dropped rather than thrown on. Kinds are stored as free text so later
 * tickets can add their own, and an event whose payload no longer parses is one bad row — neither
 * should be able to end a turn the user is watching.
 */
export const createFrameMapper = ({
  textId,
  onAssistantMessageStored,
}: {
  textId: string;
  /**
   * Told the id of the row the reply became, which is not a chunk the chat state machine has any
   * use for and is the only thing in the log that can make the streamed reply and a transcript
   * read's row recognisable as one message.
   *
   * A callback rather than a chunk because the id arrives at the end: the envelope that could
   * carry it is the `start` chunk, which is already gone by the time the worker has persisted
   * anything, and the SDK takes an id from nowhere else.
   */
  onAssistantMessageStored?: (messageId: string) => void;
}) => {
  let textOpen = false;
  let finished = false;

  const openText = (): UIMessageChunk[] => {
    if (textOpen) {
      return [];
    }

    textOpen = true;
    return [{ type: 'text-start', id: textId }];
  };

  const closeText = (): UIMessageChunk[] => {
    if (!textOpen) {
      return [];
    }

    textOpen = false;
    return [{ type: 'text-end', id: textId }];
  };

  return {
    /** The envelope the chat state machine expects around a response. */
    start: (): UIMessageChunk[] => [{ type: 'start' }],

    map: (frame: SseFrame): UIMessageChunk[] => {
      if (finished) {
        return [];
      }

      const payload = parseJson(frame.data);

      switch (frame.event) {
        case TURN_EVENT_TEXT: {
          const delta = (payload as { delta?: unknown } | null)?.delta;
          if (typeof delta !== 'string' || delta === '') {
            return [];
          }

          return [...openText(), { type: 'text-delta', id: textId, delta }];
        }

        case TURN_EVENT_STATUS: {
          const status = parseStatusEventPayload(payload);

          // Transient: it reaches `onData` and never becomes part of the message, so a replayed
          // turn does not re-narrate progress that has already happened.
          return status === null ? [] : [{ type: 'data-status', data: status, transient: true }];
        }

        /**
         * The reply has been persisted, under an id minted in the worker that nothing else tells
         * the browser.
         *
         * It produces no chunk. What it does is name the row, so the copy the chat has been
         * rendering under an id of its own can be renamed to it — the same reconciliation the
         * sent message gets from the `202` body, for the half of the conversation the worker
         * writes. Without it a transcript read that has already returned the reply leaves the
         * conversation showing the agent saying the same thing twice, which is what a page that
         * attaches on load to a reply already being written would do.
         *
         * Only the assistant's own row is worth naming here: the user's is already reconciled at
         * send time, and a role this side has no copy of has nothing to rename.
         */
        case TURN_EVENT_MESSAGE: {
          const stored = messageEventPayloadSchema.safeParse(payload);
          if (stored.success && stored.data.role === 'assistant') {
            onAssistantMessageStored?.(stored.data.message_id);
          }

          return [];
        }

        case TURN_EVENT_ARTIFACT: {
          const artifact = parseArtifactEventPayload(payload);
          if (artifact === null) {
            return [];
          }

          // The id is the reconciliation key. Successive events for one artifact replace the part
          // rather than appending one, which is the difference between watching a document being
          // written and watching a transcript of every intermediate state of it.
          return [{ type: 'data-artifact', id: artifact.artifact_id, data: artifact }];
        }

        /**
         * No tool result reaches the transcript: what a tool did reaches the screen through the
         * documents it wrote, so a tool cannot start putting its name, its arguments and its output
         * on screen.
         */
        case TURN_EVENT_TOOL_RESULT: {
          return [];
        }

        case TURN_EVENT_DONE: {
          finished = true;
          return [...closeText(), { type: 'finish' }];
        }

        case TURN_EVENT_ERROR: {
          finished = true;
          const parsed = errorEventPayloadSchema.safeParse(payload);
          const errorText = parsed.success
            ? terminalErrorMessage(parsed.data.code, parsed.data.message)
            : 'That reply could not be finished.';

          return [...closeText(), { type: 'error', errorText }, { type: 'finish' }];
        }

        default:
          return [];
      }
    },

    /**
     * Closes the stream when the connection ended without a terminal frame — a dropped socket,
     * or a tab going away. The turn itself is unaffected; the log still holds it, and reconnecting
     * replays from the last sequence number seen.
     */
    end: (): UIMessageChunk[] => (finished ? [] : [...closeText(), { type: 'finish' }]),

    get isFinished(): boolean {
      return finished;
    },
  };
};

const parseJson = (raw: string): unknown => {
  if (raw.trim() === '') {
    return null;
  }

  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};
